import { collection, getDocs } from "firebase/firestore";
import { db } from "../backend/firebaseConfig";
import { addNotification } from "../components/functions/notifyComp";

const CLINIC_NAME = "VSDiamond Dental Clinic";

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = String(dateStr).split("-");
  const date = new Date(year, month - 1, day);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${monthNames[date.getMonth()]} ${day}, ${year}`;
}

function formatTimeBlockForAppMessage(timeBlockLabel) {
  if (!timeBlockLabel || typeof timeBlockLabel !== "string") {
    return "N/A";
  }
  const parts = timeBlockLabel.split(" - ").map((p) => p.trim());
  if (parts.length !== 2) return timeBlockLabel;
  return `from ${parts[0]} to ${parts[1]}`;
}

function isEmailVerified(userData) {
  return userData?.emailVerified === "Yes" || userData?.emailVerified === true;
}

function resolveNotificationEmail(userData) {
  const email = userData?.email || userData?.authEmail || "";
  if (!email || String(email).includes("@mobileuser.dentalapp.com")) {
    return null;
  }
  return email;
}

function resolveDisplayName(userData) {
  if (!userData) return "Staff";
  if (userData.firstName && userData.lastName) {
    return `${userData.firstName} ${userData.lastName}`;
  }
  return userData.name || "Staff";
}

function getStatusChangeApiUrl() {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${base}/api/send-appointment-status-change`;
}

async function sendSecondaryDentistEmail(payload) {
  try {
    const response = await fetch(getStatusChangeApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        isSecondaryDentistNotification: true,
        clinicName: CLINIC_NAME,
        ...payload,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        "HTTP error sending secondary dentist email:",
        response.status,
        errorText
      );
      return false;
    }

    const result = await response.json();
    if (!result.success) {
      console.error(
        "Failed to send secondary dentist email:",
        result.message
      );
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error sending secondary dentist email:", error);
    return false;
  }
}

function buildAssignmentAppMessage({
  secondaryDentistName,
  primaryDentistName,
  formattedDateLong,
  timeBlockLabel,
  actorName,
}) {
  const timePhrase = formatTimeBlockForAppMessage(timeBlockLabel);
  return `${secondaryDentistName} has been assigned as the secondary dentist for ${primaryDentistName} on ${formattedDateLong}, ${timePhrase}, by ${actorName}.`;
}

function buildRemovalAppMessage({
  secondaryDentistName,
  primaryDentistName,
  formattedDateLong,
  timeBlockLabel,
  actorName,
}) {
  const timePhrase = formatTimeBlockForAppMessage(timeBlockLabel);
  return `${secondaryDentistName} has been removed as the secondary dentist for ${primaryDentistName} on ${formattedDateLong}, ${timePhrase}, by ${actorName}.`;
}

async function dispatchSecondaryDentistNotifications({
  notificationAction,
  primaryDentistUid,
  secondaryDentistUid,
  dateStr,
  timeBlockLabel,
  actorName,
  primaryDentistName,
  secondaryDentistName,
}) {
  if (
    !primaryDentistUid ||
    !secondaryDentistUid ||
    !dateStr ||
    !timeBlockLabel
  ) {
    return;
  }

  const formattedDateLong = formatDateLong(dateStr);
  const appMessage =
    notificationAction === "removed"
      ? buildRemovalAppMessage({
          secondaryDentistName,
          primaryDentistName,
          formattedDateLong,
          timeBlockLabel,
          actorName,
        })
      : buildAssignmentAppMessage({
          secondaryDentistName,
          primaryDentistName,
          formattedDateLong,
          timeBlockLabel,
          actorName,
        });

  const affectedUids = new Set([primaryDentistUid, secondaryDentistUid]);

  try {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const notifiedUserIds = new Set();

    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const data = userDoc.data();
      const userRole = data.userType || data.role;
      const isClinicStaff = userRole === "ClinicStaff";
      const isAffectedDentist = affectedUids.has(userId);

      if (!isClinicStaff && !isAffectedDentist) {
        continue;
      }
      if (notifiedUserIds.has(userId)) {
        continue;
      }
      notifiedUserIds.add(userId);

      await addNotification(userId, appMessage);

      const recipientEmail = resolveNotificationEmail(data);
      if (!recipientEmail || !isEmailVerified(data)) {
        continue;
      }

      const recipientName = resolveDisplayName(data);
      void sendSecondaryDentistEmail({
        notificationAction,
        recipientEmail,
        recipientName,
        recipientRole: userRole,
        primaryDentistName,
        secondaryDentistName,
        assignmentDate: formattedDateLong,
        timeBlockLabel,
        actorName: actorName || "Clinic Personnel",
      });
    }
  } catch (error) {
    console.error("Error sending secondary dentist notifications:", error);
  }
}

export async function sendSecondaryDentistAssignmentNotifications(params) {
  await dispatchSecondaryDentistNotifications({
    ...params,
    notificationAction: "assigned",
  });
}

export async function sendSecondaryDentistRemovalNotifications(params) {
  await dispatchSecondaryDentistNotifications({
    ...params,
    notificationAction: "removed",
  });
}
